import { computed, onScopeDispose, ref, shallowRef } from 'vue'

import { apiUrl, jobEventsUrl, jobFileUrl, readJson } from '@/lib/api'
import { ERROR_MESSAGES, errorFromCode, toApiError } from '@/lib/errors'
import { looksLikeUrlAttempt, parseYouTubeUrl } from '@/lib/youtubeUrl'
import type {
  ApiError,
  DoneEvent,
  HealthResponse,
  JobCreatedResponse,
  PreviewResponse,
  ProgressEvent,
} from '@/types'

/** The frontend state machine, mirroring the backend job lifecycle. */
export type ConversionState =
  | 'idle'
  | 'previewing'
  | 'previewed'
  | 'converting'
  | 'ready'
  | 'error'

export const PREVIEW_DEBOUNCE_MS = 400
export const HEALTH_POLL_MS = 30_000

/**
 * How many transport-level EventSource errors we tolerate before giving up.
 * EventSource reconnects on its own; we only surface a failure once it is
 * clear the stream is not coming back.
 */
export const MAX_RECONNECT_ATTEMPTS = 5

/** `EventSource.readyState` values, spelled out so mocks need no statics. */
const ES_CLOSED = 2

/** How long a download's `blob:` URL is kept alive before being revoked. */
const OBJECT_URL_TTL_MS = 60_000

/**
 * A transport failure is not a backend error code, so it maps to INTERNAL with
 * the contract's own copy. What makes it *distinct* from a server-sent `error`
 * event is the behaviour around it: EventSource is allowed to reconnect first
 * (`reconnecting`), and we flip `online` to false so the offline banner shows.
 */
const NETWORK_ERROR: ApiError = {
  code: 'INTERNAL',
  message: ERROR_MESSAGES.INTERNAL,
}

/**
 * Sentinel for "the fetch itself threw", told apart from a real error body by
 * identity. A rejected fetch has no body to parse — an expired presigned URL
 * answers 403 with an XML document that carries no `Access-Control-Allow-Origin`,
 * so the browser refuses to hand it over at all.
 */
const NETWORK_FAILURE = Symbol('network-failure')

/** The outcome of one download attempt. See `fetchDownload`. */
type DownloadAttempt =
  | { kind: 'ok'; blob: Blob }
  /** A newer job superseded this one mid-flight; say nothing. */
  | { kind: 'stale' }
  | { kind: 'failed'; body: unknown }

export function useConversion() {
  // ---------------------------------------------------------------- state --
  const url = ref('')
  const state = ref<ConversionState>('idle')
  const preview = shallowRef<PreviewResponse | null>(null)
  const progress = shallowRef<ProgressEvent | null>(null)
  const result = shallowRef<DoneEvent | null>(null)
  const error = shallowRef<ApiError | null>(null)
  const jobId = ref<string | null>(null)
  const health = shallowRef<HealthResponse | null>(null)
  const online = ref(true)
  const reconnecting = ref(false)

  // ------------------------------------------------------------ internals --
  let debounceHandle: ReturnType<typeof setTimeout> | null = null
  let previewController: AbortController | null = null
  let source: EventSource | null = null
  let healthInterval: ReturnType<typeof setInterval> | null = null

  /**
   * Monotonic sequence numbers. Every async result carries the sequence it was
   * issued under; anything stale is dropped instead of clobbering newer state.
   * AbortController alone is not enough — an already-resolved fetch still runs
   * its `.then`, and mocked fetches in tests ignore signals entirely.
   */
  let previewSeq = 0
  let jobSeq = 0

  let reconnectAttempts = 0
  let pendingImmediate = false
  let pasteAppliedValue: string | null = null
  let lastAction: 'preview' | 'convert' = 'preview'

  // --------------------------------------------------------------- derived --
  const stage = computed(() => progress.value?.stage ?? 'queued')
  const percent = computed(() => progress.value?.percent ?? 0)
  const queuePosition = computed(() => progress.value?.queuePosition ?? 0)
  const isIndeterminate = computed(() => stage.value !== 'downloading')
  /**
   * Where the finished MP3 comes from.
   *
   * The `done` event carries a presigned R2 URL when the backend stores files
   * there — that is the whole point of R2: the bytes bypass the Cloudflare
   * Tunnel. With `STORAGE_BACKEND=local` the field is absent and the API's own
   * file endpoint serves it. Both paths stay supported.
   */
  const downloadUrl = computed(() => {
    const direct = result.value?.downloadUrl
    if (typeof direct === 'string' && direct !== '') return direct
    return jobId.value ? jobFileUrl(jobId.value) : null
  })

  // ---------------------------------------------------------------- input --

  /**
   * Handle a change to the URL field.
   *
   * Cancels any in-flight preview (both via AbortController and via the
   * sequence guard) so a slow response for an older URL can never win.
   */
  function setUrl(next: string, options: { immediate?: boolean } = {}): void {
    // `handlePaste(text)` already applied this exact value, and the browser's
    // own `input` event is now echoing it back. Swallow the echo once so the
    // preview is not requested twice.
    const echoesPaste = pasteAppliedValue !== null && next.trim() === pasteAppliedValue
    pasteAppliedValue = null
    if (echoesPaste) {
      pendingImmediate = false
      return
    }

    url.value = next

    clearDebounce()
    abortPreview()
    // Editing the URL invalidates any conversion in progress.
    closeStream()
    jobId.value = null
    result.value = null
    progress.value = null
    reconnecting.value = false
    jobSeq += 1

    const immediate = options.immediate === true || pendingImmediate
    pendingImmediate = false

    const trimmed = next.trim()
    if (trimmed === '') {
      preview.value = null
      error.value = null
      state.value = 'idle'
      return
    }

    const parsed = parseYouTubeUrl(trimmed)

    if (!parsed.ok) {
      // Stay quiet until the user pauses; then explain, but only when the text
      // actually looks like an attempt at a link.
      preview.value = null
      error.value = null
      state.value = 'idle'
      const code = parsed.code
      const complain = (): void => {
        if (url.value.trim() !== trimmed) return
        if (code === 'PLAYLIST_ONLY' || looksLikeUrlAttempt(trimmed)) {
          lastAction = 'preview'
          fail(errorFromCode(code))
        }
      }
      if (immediate) complain()
      else debounceHandle = setTimeout(complain, PREVIEW_DEBOUNCE_MS)
      return
    }

    // Valid link: show the skeleton straight away, fetch after the debounce.
    preview.value = null
    error.value = null
    state.value = 'previewing'

    if (immediate) {
      void runPreview(trimmed)
    } else {
      debounceHandle = setTimeout(() => {
        void runPreview(trimmed)
      }, PREVIEW_DEBOUNCE_MS)
    }
  }

  /**
   * A paste is an explicit, complete intent — skip the debounce.
   *
   * Called from the `paste` event, which fires *before* the field updates, so
   * we only arm a flag; the `input` handler that follows does the work.
   */
  function handlePaste(text?: string): void {
    pendingImmediate = true
    if (typeof text === 'string' && text.trim() !== '') {
      const trimmed = text.trim()
      setUrl(trimmed, { immediate: true })
      pasteAppliedValue = trimmed
    }
  }

  /** Enter in the URL field: convert if we can, otherwise preview right now. */
  function submit(): void {
    if (state.value === 'previewed' && preview.value) {
      void convert()
      return
    }
    // The field stays enabled and keeps focus after Enter, so a second Enter
    // is the most natural thing in the world. It must not fall through to
    // setUrl(), which tears down the stream and drops the job or the finished
    // result on the floor with nothing on screen to explain why.
    if (
      state.value === 'previewing' ||
      state.value === 'converting' ||
      state.value === 'ready'
    ) {
      return
    }
    setUrl(url.value, { immediate: true })
  }

  // -------------------------------------------------------------- preview --
  async function runPreview(target: string): Promise<void> {
    abortPreview()

    const controller = new AbortController()
    previewController = controller
    const seq = ++previewSeq

    lastAction = 'preview'
    preview.value = null
    error.value = null
    state.value = 'previewing'

    try {
      const response = await fetch(apiUrl('/api/preview'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ url: target }),
        signal: controller.signal,
      })
      const body = await readJson(response)

      // Out-of-order guard: a newer request has since been issued.
      if (seq !== previewSeq) return

      if (!response.ok) {
        fail(toApiError(body))
        return
      }

      const data = body as PreviewResponse | null
      if (!data || typeof data.videoId !== 'string') {
        fail(errorFromCode('INTERNAL'))
        return
      }

      preview.value = data
      state.value = 'previewed'
    } catch {
      if (seq !== previewSeq || controller.signal.aborted) return
      online.value = false
      fail(NETWORK_ERROR)
      void checkHealth()
    } finally {
      if (previewController === controller) previewController = null
    }
  }

  // ------------------------------------------------------------- converting --
  async function convert(): Promise<void> {
    if (!preview.value) return

    lastAction = 'convert'
    closeStream()

    const seq = ++jobSeq
    reconnectAttempts = 0
    reconnecting.value = false
    error.value = null
    result.value = null
    jobId.value = null
    progress.value = {
      status: 'queued',
      stage: 'queued',
      percent: 0,
      downloadedBytes: 0,
      totalBytes: 0,
      queuePosition: 0,
    }
    state.value = 'converting'

    try {
      const response = await fetch(apiUrl('/api/jobs'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ url: url.value.trim() }),
      })
      const body = await readJson(response)

      if (seq !== jobSeq) return

      if (!response.ok) {
        fail(toApiError(body))
        return
      }

      const created = body as JobCreatedResponse | null
      if (!created || typeof created.jobId !== 'string' || created.jobId === '') {
        fail(errorFromCode('INTERNAL'))
        return
      }

      jobId.value = created.jobId
      openStream(created.jobId, seq)
    } catch {
      if (seq !== jobSeq) return
      online.value = false
      fail(NETWORK_ERROR)
      void checkHealth()
    }
  }

  function openStream(id: string, seq: number): void {
    const es = new EventSource(jobEventsUrl(id))
    source = es

    es.addEventListener('open', () => {
      reconnectAttempts = 0
      reconnecting.value = false
    })

    es.addEventListener('progress', (event) => {
      if (seq !== jobSeq) return
      const data = parseEventData<ProgressEvent>(event)
      if (!data) return
      reconnecting.value = false
      progress.value = data
    })

    es.addEventListener('done', (event) => {
      if (seq !== jobSeq) return
      const data = parseEventData<DoneEvent>(event)
      closeStream()
      if (!data || typeof data.filename !== 'string') {
        fail(errorFromCode('INTERNAL'))
        return
      }
      reconnecting.value = false
      result.value = data
      state.value = 'ready'
      triggerDownload()
    })

    // A server-sent `event: error` and a transport failure both land here: the
    // SSE spec dispatches named events by type, so `error` collides with the
    // built-in connection-error event. They are told apart by the payload —
    // only a MessageEvent carries `data`.
    es.addEventListener('error', (event) => {
      if (seq !== jobSeq) return

      const payload = (event as MessageEvent<unknown>).data
      if (typeof payload === 'string' && payload !== '') {
        closeStream()
        fail(toApiError(safeParse(payload)))
        return
      }

      // Transport error. `CLOSED` means EventSource has given up for good.
      if (es.readyState === ES_CLOSED) {
        closeStream()
        online.value = false
        fail(NETWORK_ERROR)
        void checkHealth()
        return
      }

      // Otherwise it is reconnecting on its own — surface it, but wait.
      reconnectAttempts += 1
      reconnecting.value = true
      if (reconnectAttempts > MAX_RECONNECT_ATTEMPTS) {
        closeStream()
        online.value = false
        fail(NETWORK_ERROR)
        void checkHealth()
      }
    })
  }

  // ------------------------------------------------------------- download --

  /**
   * Fetch the finished file, then hand it to the browser as a blob.
   *
   * Deliberately *not* a plain anchor pointing at wherever the file lives. It
   * is always a different origin — the tunnel, or R2 — where the `download`
   * attribute is ignored, so clicking such an anchor is an ordinary top-level
   * navigation: the moment the URL answers anything but 200 the browser
   * replaces the whole SPA with the error body and every bit of state is gone.
   * Both backends have that failure. `404 EXPIRED` once the sweeper has run, a
   * 502 while the home box restarts — and, on R2, a presigned URL that has
   * passed its ~10 minute TTL, which answers 403 with an XML error document.
   *
   * Fetching keeps failures inside the app (EXPIRED is rendered inline, with
   * "Try again"), and the resulting `blob:` URL is same-origin, so `download`
   * is honoured and the file is named from the `done` event. Cross-origin, it
   * needs the R2 bucket's CORS policy to allow this origin — see the README.
   *
   * A presigned R2 URL fails long before the file does: it is minted once, at
   * upload, with a ~10 minute TTL, and it is frozen into the `done` event the
   * backend replays to every later subscriber — while the job stays `ready`
   * for an hour and the R2 object lives for 24. So a "Download again" click
   * fifteen minutes later hits a dead signature for a file that is still
   * there, twice over. When the direct URL fails for any reason, this retries
   * once against `GET /api/jobs/{jobId}/file`, which serves the same file for
   * the rest of the job's TTL. Only if *that* fails is it really gone.
   */
  async function triggerDownload(): Promise<void> {
    if (typeof document === 'undefined') return

    const direct = result.value?.downloadUrl
    const viaApi = jobId.value ? jobFileUrl(jobId.value) : null
    const href = typeof direct === 'string' && direct !== '' ? direct : viaApi
    if (!href) return

    const seq = jobSeq
    let attempt = await fetchDownload(href, seq)

    if (attempt.kind === 'failed' && viaApi !== null && href !== viaApi) {
      attempt = await fetchDownload(viaApi, seq)
    }

    if (attempt.kind === 'stale') return
    if (attempt.kind === 'ok') {
      saveBlob(attempt.blob, result.value?.filename ?? 'audio.mp3')
      return
    }
    if (attempt.body === NETWORK_FAILURE) {
      // The fetch never produced a response at all. That is indistinguishable
      // from the box being unreachable, so it is reported as one.
      online.value = false
      fail(NETWORK_ERROR)
      void checkHealth()
      return
    }
    // The API's only documented failure here is 404 EXPIRED, and R2's
    // expired-signature XML parses as nothing at all — which lands on the same
    // fallback. Either way the file is gone: convert it again.
    fail(toApiError(attempt.body, 'EXPIRED'))
  }

  /**
   * One download attempt, with no state mutation — the caller decides whether
   * a failure is worth reporting or worth retrying somewhere else.
   */
  async function fetchDownload(href: string, seq: number): Promise<DownloadAttempt> {
    try {
      const response = await fetch(href, { headers: { Accept: 'audio/mpeg' } })
      if (!response.ok) {
        const body = await readJson(response)
        if (seq !== jobSeq) return { kind: 'stale' }
        return { kind: 'failed', body }
      }
      const blob = await response.blob()
      if (seq !== jobSeq) return { kind: 'stale' }
      return { kind: 'ok', blob }
    } catch {
      if (seq !== jobSeq) return { kind: 'stale' }
      return { kind: 'failed', body: NETWORK_FAILURE }
    }
  }

  // ---------------------------------------------------------------- retry --
  function retry(): void {
    error.value = null

    if (lastAction === 'convert' && preview.value) {
      void convert()
      return
    }

    const trimmed = url.value.trim()
    if (trimmed === '') {
      reset()
      return
    }

    const parsed = parseYouTubeUrl(trimmed)
    if (!parsed.ok) {
      fail(errorFromCode(parsed.code))
      return
    }

    state.value = 'previewing'
    void runPreview(trimmed)
  }

  function reset(): void {
    clearDebounce()
    abortPreview()
    closeStream()
    jobSeq += 1
    pendingImmediate = false
    pasteAppliedValue = null
    lastAction = 'preview'
    url.value = ''
    preview.value = null
    progress.value = null
    result.value = null
    error.value = null
    jobId.value = null
    reconnecting.value = false
    state.value = 'idle'
  }

  // --------------------------------------------------------------- health --
  async function checkHealth(): Promise<void> {
    try {
      const response = await fetch(apiUrl('/api/health'), {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      })
      if (!response.ok) {
        online.value = false
        return
      }
      const data = (await readJson(response)) as HealthResponse | null
      if (!data || typeof data.ok !== 'boolean') {
        online.value = false
        return
      }
      health.value = data
      online.value = data.ok
    } catch {
      health.value = null
      online.value = false
    }
  }

  // ------------------------------------------------------------- plumbing --
  function fail(apiError: ApiError): void {
    error.value = apiError
    state.value = 'error'
    reconnecting.value = false
  }

  function clearDebounce(): void {
    if (debounceHandle !== null) {
      clearTimeout(debounceHandle)
      debounceHandle = null
    }
  }

  function abortPreview(): void {
    previewSeq += 1
    if (previewController) {
      previewController.abort()
      previewController = null
    }
  }

  /** Idempotent. Called on every terminal transition *and* on scope dispose. */
  function closeStream(): void {
    if (source) {
      source.close()
      source = null
    }
  }

  /** Test seam: the live EventSource, or null. */
  function currentStream(): EventSource | null {
    return source
  }

  // ---------------------------------------------------------------- setup --
  void checkHealth()
  healthInterval = setInterval(() => {
    void checkHealth()
  }, HEALTH_POLL_MS)

  onScopeDispose(() => {
    clearDebounce()
    abortPreview()
    closeStream()
    // Anything still in flight (a pending POST /api/jobs, a download) belongs
    // to a scope that no longer exists. Bumping the sequence makes those
    // continuations short-circuit instead of opening an EventSource nothing
    // will ever close.
    jobSeq += 1
    if (healthInterval !== null) {
      clearInterval(healthInterval)
      healthInterval = null
    }
  })

  return {
    // state
    url,
    state,
    preview,
    progress,
    result,
    error,
    jobId,
    health,
    online,
    reconnecting,
    // derived
    stage,
    percent,
    queuePosition,
    isIndeterminate,
    downloadUrl,
    // actions
    setUrl,
    handlePaste,
    submit,
    convert,
    retry,
    reset,
    checkHealth,
    triggerDownload,
    // test seam
    currentStream,
  }
}

export type UseConversion = ReturnType<typeof useConversion>

/**
 * Hand a blob to the browser as a download.
 *
 * The object URL is revoked on a timer rather than immediately: revoking it
 * synchronously after the click cancels the download that click just started.
 */
function saveBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = filename
  anchor.rel = 'noopener'
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  setTimeout(() => {
    URL.revokeObjectURL(objectUrl)
  }, OBJECT_URL_TTL_MS)
}

function parseEventData<T>(event: Event): T | null {
  const raw = (event as MessageEvent<unknown>).data
  if (typeof raw !== 'string' || raw === '') return null
  const parsed = safeParse(raw)
  return parsed === null ? null : (parsed as T)
}

function safeParse(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown
  } catch {
    return null
  }
}
